import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { dateTime } from "@/lib/format";
import { PageTitle } from "@/components/brand";
import { useAuth } from "@/context/AuthContext";

const num = (v) => (v === "" || v == null ? undefined : Number(v));

const RateRow = ({ fid, k, label, fields, current, suspended, onSaved }) => {
  const [f, setF] = useState({});
  const save = async (extra = {}) => {
    const body = { key: k, ...Object.fromEntries(Object.entries(f).map(([a, b]) => [a, num(b)]).filter(([, b]) => b !== undefined)), ...extra };
    try { await api.put(`/admin/exchange/${fid}/override`, body); toast.success("Saved"); setF({}); onSaved(); } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <div data-testid={`rate-row-${k}`} className="flex flex-wrap items-center gap-2 border-t border-cr-line py-2 text-sm">
      <span className="w-72 truncate font-semibold">{label}</span>
      {fields.map((x) => <input key={x} aria-label={x} data-testid={`rate-${k}-${x}`} placeholder={`${x} ${current[x] ?? "-"}`} value={f[x] ?? ""} onChange={(e) => setF({ ...f, [x]: e.target.value })} className="input-cr w-24 py-1.5 text-xs" />)}
      <button data-testid={`rate-save-${k}`} onClick={() => save()} className="btn-gold px-3 py-1.5 text-xs normal-case">Set</button>
      <button data-testid={`rate-suspend-${k}`} onClick={() => save({ suspended: !suspended })} className={`btn-outline px-3 py-1.5 text-xs normal-case ${suspended ? "text-emerald-400" : "text-cr-danger"}`}>{suspended ? "Open" : "Suspend"}</button>
      <button data-testid={`rate-reset-${k}`} onClick={() => save({ reset: true })} className="text-xs text-cr-muted hover:text-cr-ink">Auto</button>
    </div>
  );
};

const RateControl = () => {
  const [matches, setMatches] = useState([]);
  const [fid, setFid] = useState("");
  const [d, setD] = useState(null);
  useEffect(() => { Promise.all([api.get("/cricket/matches?status=live"), api.get("/cricket/matches?status=upcoming")]).then(([a, b]) => setMatches([...a.data, ...b.data.filter((m) => m.can_predict)])); }, []);
  const load = () => fid && api.get(`/admin/exchange/${fid}`).then((r) => setD(r.data));
  useEffect(() => { setD(null); load(); }, [fid]); // eslint-disable-line react-hooks/exhaustive-deps
  const ov = Object.fromEntries((d?.overrides || []).map((o) => [o.key, o]));
  return (
    <div data-testid="rate-control" className="card-gold mb-8 p-6">
      <h2 className="text-lg font-bold">Rate control (all admins)</h2>
      <p className="mb-3 text-xs text-cr-muted">Rates come from the live score automatically. Set a value to override it, suspend a market, or press Auto to go back to automatic.</p>
      <select data-testid="rate-match" value={fid} onChange={(e) => setFid(e.target.value)} className="input-cr mb-4"><option value="">Select live / upcoming match</option>
        {matches.map((m) => <option key={m.id} value={m.id}>{m.is_live ? "LIVE · " : ""}{m.home.name} vs {m.away.name} · {dateTime(m.start)}</option>)}</select>
      {d && <>
        <RateRow fid={fid} k="all" label="Whole match" fields={[]} current={{}} suspended={ov.all?.suspended} onSaved={load} />
        {d.odds.map((r) => <RateRow key={r.side} fid={fid} k={`odds:${r.side}`} label={`${r.name} · win chance ${r.prob}%`} fields={["lagai", "khai", "boost"]} current={r} suspended={ov[`odds:${r.side}`]?.suspended} onSaved={load} />)}
        {d.sessions.map((s) => <RateRow key={s.key} fid={fid} k={s.key} label={s.name} fields={["no", "yes", "no_rate", "yes_rate"]} current={s} suspended={s.suspended} onSaved={load} />)}
      </>}
    </div>
  );
};

const Cashback = () => {
  const [pct, setPct] = useState("");
  const [mg, setMg] = useState("");
  const [cur, setCur] = useState({ cashback_pct: 0, margin_pct: 5 });
  useEffect(() => { api.get("/admin/exchange/promo").then((r) => setCur(r.data)); }, []);
  const save = async () => {
    try {
      const r = await api.put("/admin/exchange/promo", { cashback_pct: pct === "" ? cur.cashback_pct : Number(pct), margin_pct: mg === "" ? cur.margin_pct : Number(mg) });
      setCur(r.data); setPct(""); setMg(""); toast.success("Saved");
    } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <div data-testid="cashback-control" className="card-gold mb-8 p-6">
      <h2 className="text-lg font-bold">Rates & rewards</h2>
      <p className="mb-3 text-xs text-cr-muted">Rate margin: platform edge built into every Lagai/Khai rate on all matches (higher = lower payouts for users, 0 = fair rate). Cashback: % of every lost stake returned to the user. Boost: per team in Rate control below.</p>
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <span>Margin now <b data-testid="margin-current" className="text-cr-gold">{cur.margin_pct}%</b></span>
        <input data-testid="margin-input" type="number" min="0" max="20" placeholder="0-20" value={mg} onChange={(e) => setMg(e.target.value)} className="input-cr w-24 py-1.5 text-xs" />
        <span className="ml-4">Cashback now <b data-testid="cashback-current" className="text-cr-gold">{cur.cashback_pct}%</b></span>
        <input data-testid="cashback-input" type="number" min="0" max="25" placeholder="0-25" value={pct} onChange={(e) => setPct(e.target.value)} className="input-cr w-24 py-1.5 text-xs" />
        <button data-testid="cashback-save" onClick={save} disabled={pct === "" && mg === ""} className="btn-gold px-3 py-1.5 text-xs normal-case">Save</button>
      </div>
    </div>
  );
};

const SettleMatch = ({ f, onDone }) => {
  const [winner, setWinner] = useState("");
  const go = async () => {
    const label = winner === "void" ? "VOID (refund everyone)" : `${f.odds.find((o) => o.side === winner)?.name} WON`;
    if (!window.confirm(`Settle all open Lagai/Khai bets on ${f.match} as ${label}?`)) return;
    try { const r = await api.post(`/admin/exchange/match/${f.fixture_id}/settle`, { winner }); toast.success(`${r.data.settled} bets settled · ${r.data.won} won, ${r.data.lost} lost, ${r.data.void} void`); setWinner(""); onDone(); } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-cr-line pt-3">
      <select data-testid={`settle-match-winner-${f.fixture_id}`} value={winner} onChange={(e) => setWinner(e.target.value)} className="input-cr flex-1 py-1.5 text-xs">
        <option value="">Settle whole match: pick winner</option>
        {f.odds.map((o) => <option key={o.side} value={o.side}>{o.name} won</option>)}
        <option value="void">Void (refund all)</option>
      </select>
      <button data-testid={`settle-match-btn-${f.fixture_id}`} onClick={go} disabled={!winner} className="btn-gold px-3 py-1.5 text-xs normal-case">Settle match</button>
    </div>
  );
};

const Book = ({ isSuper, onSettled }) => {
  const [rows, setRows] = useState(null);
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const load = () => api.get("/admin/exchange/book").then((r) => setRows(r.data)).catch(() => setRows([]));
    load();
    const t = setInterval(load, 3000);
    return () => clearInterval(t);
  }, [tick]);
  const pl = (v) => <b className={v >= 0 ? "text-emerald-400" : "text-cr-danger"}>{v >= 0 ? "+" : ""}{v.toLocaleString("en-IN")}</b>;
  return (
    <div data-testid="exchange-book" className="mb-8">
      <h2 className="mb-1 text-lg font-bold">Book (who wins if...)</h2>
      <p className="mb-3 text-xs text-cr-muted">Green = house (admin) wins coins, red = users win. Based on all open bets.</p>
      {rows?.length === 0 && <p className="card-cr p-4 text-sm text-cr-muted">No open bets.</p>}
      <div className="grid gap-4 lg:grid-cols-2">{rows?.map((f) => (
        <div key={f.fixture_id} data-testid={`book-${f.fixture_id}`} className="card-cr p-4 text-sm">
          <p className="font-semibold">{f.match} <span className="text-xs text-cr-muted">· {f.bets} open bets</span></p>
          {f.odds.length > 0 && <div className="mt-3 space-y-1">{f.odds.map((o) => <p key={o.side} className="flex justify-between"><span>If <b>{o.name}</b> wins</span>{pl(o.house)}</p>)}</div>}
          {f.sessions.map((s) => (
            <div key={s.key} className="mt-3 border-t border-cr-line pt-2"><p className="text-xs font-semibold">{s.name} <span className="text-cr-muted">({s.count})</span></p>
              {s.outcomes.map((o) => <p key={o.if_runs} className="flex justify-between text-xs"><span>Runs {o.if_runs}</span>{pl(o.house)}</p>)}</div>))}
          {isSuper && f.odds.length > 0 && <SettleMatch f={f} onDone={() => { setTick((t) => t + 1); onSettled(); }} />}
        </div>))}</div>
    </div>
  );
};

const SettleButtons = ({ bet, onDone }) => {
  const settle = async (result) => {
    const msg = result === "won" ? `Mark WON and credit ${bet.amount + bet.win} coins to ${bet.user_email}?` : result === "lost" ? "Mark LOST? Stake goes to the admin." : "Mark VOID? Stake is refunded to the user.";
    if (!window.confirm(msg)) return;
    try { await api.post(`/admin/exchange/bets/${bet.id}/settle`, { result }); toast.success(`Bet marked ${result}`); onDone(); } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <div className="flex gap-1">
      <button data-testid={`settle-won-${bet.id}`} onClick={() => settle("won")} className="rounded bg-emerald-500 px-2 py-1 text-xs font-bold text-black">Win</button>
      <button data-testid={`settle-lost-${bet.id}`} onClick={() => settle("lost")} className="rounded bg-cr-danger px-2 py-1 text-xs font-bold text-white">Lose</button>
      <button data-testid={`settle-void-${bet.id}`} onClick={() => settle("void")} className="rounded border border-cr-line px-2 py-1 text-xs">Void</button>
    </div>
  );
};

const ChangeResult = ({ bet, onDone }) => {
  const change = async (result) => {
    const reason = window.prompt(`Change ${bet.user_email}'s bet from ${bet.status.toUpperCase()} to ${result.toUpperCase()}? Coins will be adjusted and the user will be notified. Reason:`);
    if (reason === null) return;
    if (reason.trim().length < 3) { toast.error("Reason must be at least 3 characters"); return; }
    try { await api.post(`/admin/exchange/bets/${bet.id}/change`, { result, reason }); toast.success(`Result changed to ${result}`); onDone(); } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <div className="flex flex-col gap-1">
      <div className="flex gap-1">{["won", "lost", "void"].filter((r) => r !== bet.status).map((r) => (
        <button key={r} data-testid={`change-${r}-${bet.id}`} onClick={() => change(r)} className="rounded border border-cr-gold/50 px-2 py-1 text-xs capitalize text-cr-gold">→ {r === "won" ? "Win" : r === "lost" ? "Lose" : "Void"}</button>))}</div>
      {bet.corrections?.length > 0 && <span data-testid={`corrected-${bet.id}`} className="text-[10px] text-cr-muted">Corrected {bet.corrections.length}×</span>}
    </div>
  );
};

const TAGS = { review: "Review", suspicious: "Suspicious", vip: "VIP", stale_rate: "Stale rate" };

const NoteCell = ({ bet, isSuper, onDone }) => {
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState(bet.admin_note || "");
  const [tag, setTag] = useState(bet.admin_tag || "");
  const save = async () => {
    try { await api.post(`/admin/exchange/bets/${bet.id}/note`, { note, tag }); toast.success("Admin note saved"); setOpen(false); onDone(); } catch (e) { toast.error(apiError(e)); }
  };
  if (open) return (
    <div className="flex min-w-[220px] flex-col gap-1">
      <select data-testid={`note-tag-${bet.id}`} value={tag} onChange={(e) => setTag(e.target.value)} className="input-cr py-1 text-xs"><option value="">No tag</option>{Object.entries(TAGS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select>
      <input data-testid={`note-input-${bet.id}`} value={note} maxLength={300} onChange={(e) => setNote(e.target.value)} placeholder="Note for admins" className="input-cr py-1 text-xs" />
      <div className="flex gap-1"><button data-testid={`note-save-${bet.id}`} onClick={save} className="btn-gold px-2 py-1 text-xs normal-case">Save</button><button data-testid={`note-cancel-${bet.id}`} onClick={() => setOpen(false)} className="text-xs text-cr-muted">Cancel</button></div>
    </div>
  );
  return (
    <div data-testid={`note-view-${bet.id}`} className="max-w-[220px] text-xs">
      {bet.admin_tag && <span data-testid={`note-tag-badge-${bet.id}`} className={`mr-1 rounded px-1.5 py-0.5 font-bold ${bet.admin_tag === "suspicious" ? "bg-cr-danger text-white" : "bg-cr-gold text-black"}`}>{TAGS[bet.admin_tag]}</span>}
      {bet.admin_note && <span data-testid={`note-text-${bet.id}`} className="text-cr-muted">{bet.admin_note}</span>}
      {!bet.admin_tag && !bet.admin_note && !isSuper && "-"}
      {isSuper && <button data-testid={`note-edit-${bet.id}`} onClick={() => setOpen(true)} className="ml-1 text-cr-gold underline">{bet.admin_note || bet.admin_tag ? "Edit" : "Add note"}</button>}
    </div>
  );
};

export default function AdminExchange() {
  const { user } = useAuth();
  const [status, setStatus] = useState("pending");
  const [d, setD] = useState(null);
  const [tick, setTick] = useState(0);
  const isSuper = user?.role === "super_admin";
  const seen = useRef(null);
  const [fresh, setFresh] = useState({});
  useEffect(() => {
    const load = () => api.get("/admin/exchange/bets", { params: { status } }).then((r) => {
      const ids = r.data.bets.map((b) => b.id);
      if (seen.current) {
        const n = ids.filter((i) => !seen.current.has(i));
        if (n.length) { setFresh(Object.fromEntries(n.map((i) => [i, true]))); setTimeout(() => setFresh({}), 4000); toast.info(`${n.length} new bet${n.length > 1 ? "s" : ""} placed`); }
      }
      seen.current = new Set(ids);
      setD(r.data);
    }).catch(() => {});
    seen.current = null;
    load();
    const t = setInterval(load, 3000);
    return () => clearInterval(t);
  }, [status, tick]);
  return (
    <div data-testid="admin-exchange-page">
      <PageTitle crumbs={["Admin", "Live Bets"]} title="Live Bets" subtitle="Match odds (Lagai/Khai) and session bets. Winnings are reserved from the admin's coins when a bet is placed, and lost stakes come back to the admin." />
      {user?.role === "super_admin" && <><Cashback /><RateControl /></>}
      <Book isSuper={isSuper} onSettled={() => setTick((t) => t + 1)} />
      {d && <div className="mb-4 grid gap-3 sm:grid-cols-2">
        <div className="card-cr p-4"><p className="text-xs text-cr-muted">Open stakes</p><p data-testid="exposure-stake" className="font-display text-2xl font-bold">{d.exposure.stake.toLocaleString("en-IN")}</p></div>
        <div className="card-cr p-4"><p className="text-xs text-cr-muted">Reserved for payouts</p><p data-testid="exposure-liability" className="font-display text-2xl font-bold text-cr-gold">{d.exposure.liability.toLocaleString("en-IN")}</p></div>
      </div>}
      <div className="mb-4 flex gap-2">{["pending", "won", "lost", "void", "all"].map((s) => (
        <button key={s} data-testid={`xbet-filter-${s}`} onClick={() => setStatus(s)} className={`rounded-full border px-4 py-1.5 text-sm font-semibold capitalize ${status === s ? "border-cr-gold bg-cr-gold text-black" : "border-cr-line"}`}>{s}</button>))}</div>
      <p data-testid="live-monitor-indicator" className="mb-2 flex items-center gap-2 text-xs text-cr-muted"><span className="h-2 w-2 animate-pulse rounded-full bg-red-500" />Live monitor · new bets appear automatically every 3 seconds</p>
      <div className="card-cr overflow-x-auto">
        <table className="w-full min-w-[1080px] text-sm">
          <thead className="text-left text-xs uppercase text-cr-muted"><tr>{["User", "Match", "Market", "Side", "Rate", "Amount", "Win", "Status", "Time", "Admin note", ...(isSuper ? ["Settle"] : [])].map((h) => <th key={h} className="px-4 py-3">{h}</th>)}</tr></thead>
          <tbody>{d?.bets.map((b) => (
            <tr key={b.id} data-testid={`admin-xbet-row-${b.id}`} className={`border-t border-cr-line transition-colors duration-1000 ${fresh[b.id] ? "bg-cr-gold/20" : ""} ${b.side === "lagai" || b.side === "yes" ? "border-l-4 border-l-[#72bbef]" : "border-l-4 border-l-[#faa9ba]"}`}><td className="px-4 py-3">{b.user_email}</td><td className="px-4 py-3">{b.match}</td><td className="px-4 py-3">{b.name}</td>
              <td className="px-4 py-3 font-semibold uppercase">{b.side}</td><td className="px-4 py-3">{b.line != null ? `${b.line} / ${b.rate}` : b.rate}</td>
              <td className="px-4 py-3">{b.amount}</td><td className="px-4 py-3 text-cr-gold">{b.win}</td><td className="px-4 py-3 capitalize">{b.status}</td><td className="px-4 py-3 text-cr-muted">{dateTime(b.created_at)}</td>
              <td className="px-4 py-3"><NoteCell key={`${b.id}-${b.admin_note}-${b.admin_tag}`} bet={b} isSuper={isSuper} onDone={() => setTick((t) => t + 1)} /></td>
              {isSuper && <td className="px-4 py-3">{b.status === "pending" ? <SettleButtons bet={b} onDone={() => setTick((t) => t + 1)} /> : ["won", "lost", "void"].includes(b.status) ? <ChangeResult bet={b} onDone={() => setTick((t) => t + 1)} /> : "-"}</td>}</tr>))}</tbody>
        </table>
      </div>
    </div>
  );
}
